import React from "react";
import { parseBrazilDate, formatDate } from "../../utils/date";
import { useLanguage } from "../../../contexts/LanguageContext";
import { apptLabels } from "../labels";
import { SLOT_STEP } from "../utils";
import { AppointmentCard } from "./AppointmentCard";
import { UserX, Building2, Clock, Ban } from "lucide-react";
import { cn } from "../../../lib/utils";

interface Item {
  id: number | string;
  start_datetime: string;
  duration_minutes: number;
  client_name?: string;
  employee_name?: string;
  employee_color?: string | null;
  notes?: string | null;
  done?: boolean;
  evolution?: string | null;
  specialty_name?: string | null;
  has_late_payment?: boolean;
  is_exception?: boolean;
  description?: string | null;
  employee_id?: number | null;
  insurance_provider_id?: number | null;
  insurance_provider_name?: string | null;
}

interface Exception {
    id: number;
    employee_id: number | null;
    start_datetime: string;
    end_datetime: string;
    description: string | null;
    repeat_weekday: number | null;
    repeat_month_day: number | null;
}

interface Professional {
    id: number;
    name: string;
    color?: string | null;
}

interface Props {
  startDate: Date; // Should be a Sunday
  items: Item[];
  exceptions?: Exception[];
  professionals?: Professional[];
  onEdit?: (it: any) => void;
  onMove?: (it: any, newStart: Date, newEnd: Date) => void | Promise<void>;
  onMarkDone?: (it: any) => void;
  onUnmarkDone?: (it: any) => void;
  onDelete?: (it: any) => void;
  onFillEvolution?: (it: any) => void;
  startHour?: number;
  endHour?: number;
  showSaturday?: boolean;
  showSunday?: boolean;
  showCompanyBlocks?: boolean;
  showProfessionalBlocks?: boolean;
}

const parse = (s: string) => parseBrazilDate(s);
const minutesSinceMidnightUTC = (d: Date) => {
  return d.getUTCHours() * 60 + d.getUTCMinutes();
};

export function WeeklySchedule({
  startDate,
  items,
  exceptions = [],
  professionals = [],
  onEdit,
  onMove,
  onMarkDone,
  onUnmarkDone,
  onDelete,
  onFillEvolution,
  startHour = 7,
  endHour = 20,
  showSaturday = true,
  showSunday = true,
  showCompanyBlocks = true,
  showProfessionalBlocks = true,
}: Props) {
  const { lang } = useLanguage();
  const t = apptLabels[lang].daySchedule;
  const slotHeight = 80; // px per 30 min

  const weekDays = React.useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      const dayIndex = d.getDay(); // 0=Sunday, 6=Saturday
      if (dayIndex === 0 && !showSunday) continue;
      if (dayIndex === 6 && !showSaturday) continue;
      days.push(d);
    }
    return days;
  }, [startDate, showSaturday, showSunday]);

  const effectiveStartHour = React.useMemo(() => {
    let minItemH = 24;
    const utcStart = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()));
    const utcEnd = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 7));
    
    [...items, ...exceptions].forEach(it => {
      const d = parse(it.start_datetime);
      if (d >= utcStart && d < utcEnd) {
        const h = d.getUTCHours();
        const m = d.getUTCMinutes();

        // Ignora blocos de dia inteiro (geralmente indicando "fechado") para não forçar a agenda a começar à meia-noite
        let isAllDay = false;
        if (h === 0 && m === 0) {
            const dur = (it as any).duration_minutes || 
                        ((it as any).end_datetime ? (parse((it as any).end_datetime).getTime() - d.getTime()) / 60000 : 0);
            if (dur > 1200) isAllDay = true;
        }
        if (isAllDay) return;

        if (h < minItemH) minItemH = h;
      }
    });

    if (minItemH === 24) return startHour;
    // Retorna o menor entre o horário de abertura padrão (startHour) e o item mais cedo menos 1 hora
    return Math.max(0, Math.min(startHour, minItemH - 1));
  }, [items, exceptions, startDate, startHour]);

  const effectiveEndHour = React.useMemo(() => {
    let maxItemH = 0;
    const utcStart = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()));
    const utcEnd = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 7));
    
    [...items, ...exceptions].forEach(it => {
      const d = parse(it.start_datetime);
      if (d >= utcStart && d < utcEnd) {
        const dur = (it as any).duration_minutes || 
                    ((it as any).end_datetime ? (parse((it as any).end_datetime).getTime() - d.getTime()) / 60000 : 60);
        
        // Ignora blocos de dia inteiro
        if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && dur > 1200) return;

        const end = new Date(d.getTime() + dur * 60000);
        const eh = end.getUTCHours();
        const em = end.getUTCMinutes();
        let itemLastH = em > 0 ? eh : eh - 1;

        if (itemLastH > maxItemH) maxItemH = itemLastH;
      }
    });

    if (maxItemH === 0) return endHour;
    // Retorna o maior entre o padrão (20h) e o último item + 1 hora
    return Math.min(23, Math.max(endHour, maxItemH + 1));
  }, [items, exceptions, startDate, endHour]);

  const hours = React.useMemo(() => {
    const arr = [];
    for (let h = effectiveStartHour; h <= effectiveEndHour; h++) arr.push(h);
    return arr;
  }, [effectiveStartHour, effectiveEndHour]);

  const getDayExceptions = (dayDate: Date) => {
    const weekday = dayDate.getDay();
    const dayOfMonth = dayDate.getDate();
    const dStr = `${dayDate.getFullYear()}-${(dayDate.getMonth() + 1).toString().padStart(2, '0')}-${dayDate.getDate().toString().padStart(2, '0')}`;

    return exceptions.filter(e => {
        if (e.repeat_weekday !== null && e.repeat_weekday === weekday) return true;
        if (e.repeat_month_day !== null && e.repeat_month_day === dayOfMonth) return true;
        if (e.start_datetime.startsWith(dStr)) return true;
        return false;
    });
  };

  const getDayItems = (dayDate: Date) => {
    const dayStart = new Date(Date.UTC(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), 0, 0, 0));
    const dayEnd = new Date(Date.UTC(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), 23, 59, 59, 999));
    const dStr = `${dayDate.getFullYear()}-${(dayDate.getMonth() + 1).toString().padStart(2, '0')}-${dayDate.getDate().toString().padStart(2, '0')}`;

    const dayExcs = getDayExceptions(dayDate);
    
    const excItems: Item[] = dayExcs
      .filter(e => {
          if (e.employee_id === null) return showCompanyBlocks;
          return showProfessionalBlocks;
      })
      .map(e => {
        const origStart = parse(e.start_datetime);
        const origEnd = parse(e.end_datetime);
        let duration = Math.round((origEnd.getTime() - origStart.getTime()) / 60000);
        
        const HH = origStart.getUTCHours().toString().padStart(2, '0');
        const MM = origStart.getUTCMinutes().toString().padStart(2, '0');
        const normalizedStartStr = `${dStr} ${HH}:${MM}:00`;
        
        const startMins = origStart.getUTCHours() * 60 + origStart.getUTCMinutes();
        if (startMins + duration > 1440) duration = 1440 - startMins;

        const prof = professionals.find(p => p.id === e.employee_id);

        return {
            id: `exc-${e.id}`,
            start_datetime: normalizedStartStr,
            duration_minutes: duration,
            is_exception: true,
            description: e.description,
            employee_name: e.employee_id ? (prof?.name || "Profissional") : "Empresa",
            employee_id: e.employee_id,
            employee_color: "#ef4444"
        };
    });

    const combined = [...items, ...excItems];
    const filtered = combined.filter(it => {
        const s = parse(it.start_datetime);
        return s >= dayStart && s <= dayEnd;
    });

    type Aug = Item & {
        _start: Date;
        _startMin: number;
        _endMin: number;
        _col?: number;
        _cols?: number;
        _group?: number;
        _excIdx?: number; // Special index for exceptions
      };
  
      const arr = filtered
        .map((it) => ({ ...it, _start: parse(it.start_datetime) }))
        .sort((a, b) => {
            // Exceptions always come first to be on the leftmost side
            if (a.is_exception && !b.is_exception) return -1;
            if (!a.is_exception && b.is_exception) return 1;
            return Number(a._start) - Number(b._start);
        }) as Aug[];
  
      const out = arr as Aug[];
  
      let group = 0;
      const active: Aug[] = [];
      const usedCols = new Set<number>();
      const groupMax = new Map<number, number>();
      
      // We'll separate exceptions from normal items for layout logic
      const activeExceptions: Aug[] = [];
  
      for (const a of out) {
        a._startMin = minutesSinceMidnightUTC(a._start);
        a._endMin = a._startMin + (a.duration_minutes || 60);
        
        // Cleanup inactive items
        for (let i = active.length - 1; i >= 0; i--) {
          if ((active[i]._endMin || 0) <= a._startMin) {
            usedCols.delete(active[i]._col!);
            active.splice(i, 1);
          }
        }
        // Cleanup inactive exceptions
        for (let i = activeExceptions.length - 1; i >= 0; i--) {
            if (activeExceptions[i]._endMin <= a._startMin) {
                activeExceptions.splice(i, 1);
            }
        }

        if (active.length === 0) group++;
        a._group = group;

        if (a.is_exception) {
            // Find the first free slot among exceptions only
            let excIdx = 0;
            while (activeExceptions.some(e => e._excIdx === excIdx)) excIdx++;
            a._excIdx = excIdx;
            activeExceptions.push(a);
            // Exceptions don't consume "cols" from the main appointment layout
            // They just sit on the left.
            a._col = 0; 
        } else {
            let col = 0;
            while (usedCols.has(col)) col++;
            a._col = col;
            usedCols.add(col);
        }
        
        active.push(a);
        const maxCols = Math.max(groupMax.get(group) || 0, usedCols.size);
        groupMax.set(group, maxCols);
      }
      for (const a of out) a._cols = groupMax.get(a._group!) || 1;
      return out;
  };

  const hexToRgba = (hex?: string | null, alpha = 0.18) => {
    if (!hex) return `rgba(59,130,246,${alpha})`;
    const h = hex.replace("#", "");
    if (h.length !== 6) return `rgba(59,130,246,${alpha})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const scrollerRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (scrollerRef.current) {
      const currentHour = now.getHours();
      const scrollHour = currentHour > effectiveStartHour ? currentHour - 1 : effectiveStartHour;
      const top = (scrollHour - effectiveStartHour) * slotHeight * 2;
      scrollerRef.current.scrollTop = top;
    }
  }, [startDate]);

  const nowMin = now.getHours() * 60 + now.getMinutes();
  const showNowLine = nowMin >= effectiveStartHour * 60 && nowMin <= (endHour + 1) * 60;
  const nowTop = ((nowMin - effectiveStartHour * 60) / 30) * slotHeight;

  return (
    <div className="flex flex-col h-full bg-white border rounded-xl overflow-hidden shadow-sm">
      {/* Body: Timeline + Columns */}
      <div className="flex-1 overflow-y-auto relative min-h-0" ref={scrollerRef}>
        {/* Header: Days of the week (Sticky) */}
        <div className="sticky top-0 z-30 grid grid-cols-[72px_1fr] border-b bg-white/95 backdrop-blur-sm">
          <div className="border-r" />
          <div className="grid" style={{ gridTemplateColumns: `repeat(${weekDays.length}, 1fr)` }}>
            {weekDays.map((day, i) => {
                const dayExcs = getDayExceptions(day);
                const isFullDayCompanyClosed = showCompanyBlocks && dayExcs.some(e => {
                    if (e.employee_id !== null) return false;
                    const s = parse(e.start_datetime);
                    const e_ = parse(e.end_datetime);
                    const duration = Math.round((e_.getTime() - s.getTime()) / 60000);
                    const startMins = s.getUTCHours() * 60 + s.getUTCMinutes();
                    return startMins === 0 && duration >= 1430;
                });

                return (
                    <div key={i} className="py-2 px-1 text-center border-r last:border-r-0 flex flex-col items-center gap-1">
                        <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                        {new Intl.DateTimeFormat(lang === "pt" ? "pt-BR" : "en-US", { weekday: "short" }).format(day)}
                        </div>
                        <div className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-base font-bold ${
                            day.toDateString() === now.toDateString() 
                            ? "bg-blue-600 text-white shadow-sm" 
                            : "text-neutral-900"
                        }`}>
                        {day.getDate()}
                        </div>
                        <div className="flex flex-wrap justify-center gap-1 min-h-[16px]">
                            {isFullDayCompanyClosed && (
                                <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[8px] font-bold uppercase whitespace-nowrap">
                                    <Building2 className="h-2 w-2" />
                                    <span>FECHADO</span>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
          </div>
        </div>

        <div className="grid grid-cols-[72px_1fr] relative">
          <div className="bg-neutral-50/20 border-r">
            {hours.map((h, i) => (
              <div key={i} className="flex items-start justify-end pr-3 text-[11px] font-medium text-neutral-400 select-none" style={{ height: slotHeight * 2 }}>
                <span className="mt-[-6px] bg-white px-1 z-10">{String(h).padStart(2, "0")}:00</span>
              </div>
            ))}
          </div>

          <div className="grid relative overflow-hidden" style={{ gridTemplateColumns: `repeat(${weekDays.length}, 1fr)` }}>
            <div className="absolute inset-0 pointer-events-none">
              {hours.map((_, i) => (
                <React.Fragment key={i}>
                  <div className="border-t border-neutral-200" style={{ height: slotHeight }} />
                  <div className="border-t border-neutral-100 border-dashed" style={{ height: slotHeight }} />
                </React.Fragment>
              ))}
              <div className="border-t border-neutral-200" />
            </div>

            {showNowLine && (
              <div 
                className="absolute left-0 right-0 z-20 pointer-events-none flex items-center"
                style={{ top: nowTop }}
              >
                <div className="h-2 w-2 rounded-full bg-red-500 -ml-1" />
                <div className="h-[2px] flex-1 bg-red-500 shadow-sm" />
              </div>
            )}

            {weekDays.map((day, dayIndex) => {
              const dayItems = getDayItems(day);
              const isToday = day.toDateString() === now.toDateString();

              return (
                <div key={dayIndex} className={cn(
                    "relative border-r last:border-r-0 h-full transition-colors",
                    isToday ? "bg-blue-50/10" : ""
                )}>
                  {dayItems.map((a) => {
                    const timelineStartMin = effectiveStartHour * 60;
                    const timelineEndMin = (endHour + 1) * 60;

                    // Filter out items completely outside the visible range
                    if (a._startMin >= timelineEndMin || a._endMin <= timelineStartMin) return null;

                    const displayStartMin = Math.max(timelineStartMin, a._startMin);
                    const displayEndMin = Math.min(timelineEndMin, a._endMin);
                    
                    const startMin = displayStartMin - timelineStartMin;
                    const curDur = Math.max(SLOT_STEP, displayEndMin - displayStartMin);
                    
                    const top = (startMin / 30) * slotHeight;
                    const height = (curDur / 30) * slotHeight;

                    const cols = Math.max(1, a._cols || 1);
                    const col = Math.max(0, a._col || 0);
                    const gutter = 2;
                    const excWidth = 28;

                    if (a.is_exception) {
                        const excIdx = a._excIdx || 0;
                        return (
                            <div
                              key={a.id}
                              className="absolute z-20 transition-all duration-200"
                              style={{
                                top,
                                height,
                                // Stack exceptions side-by-side on the left
                                left: `${excIdx * excWidth + gutter}px`,
                                width: `${excWidth}px`,
                              }}
                            >
                                <div className={cn(
                                    "h-full w-full rounded-md border border-red-300 flex flex-col items-center pt-2 pb-1 shadow-sm overflow-hidden",
                                    a.employee_id === null ? "bg-red-50/90" : "bg-neutral-50/90"
                                )}>
                                    <div className="flex flex-col items-center gap-2 h-full">
                                        <div className="text-red-600">
                                            {a.employee_id === null ? <Building2 className="h-3.5 w-3.5" /> : <UserX className="h-3.5 w-3.5" />}
                                        </div>
                                        
                                        <div className="flex-1 overflow-hidden">
                                            <div 
                                                className="text-[9px] font-bold text-red-600 uppercase tracking-tighter whitespace-nowrap"
                                                style={{ 
                                                    writingMode: 'vertical-rl',
                                                    transform: 'rotate(180deg)',
                                                }}
                                            >
                                                {a.employee_id === null ? "FECHADO" : a.employee_name?.split(' ')[0]}
                                            </div>
                                        </div>
                                        
                                        <Ban className="h-3 w-3 text-red-200 mt-auto" />
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    const colr = a.employee_color;
                    const bg = hexToRgba(colr, a.done ? 0.22 : 0.16);

                    // Appointments shift to the right based on how many exceptions are active
                    // But to keep it simple, we'll just overlay them or slightly offset them
                    // Let's check how many active exceptions overlap with this appointment at its start
                    const activeExcsAtStart = dayItems.filter(e => e.is_exception && e._startMin <= a._startMin && e._endMin > a._startMin).length;
                    const offsetLeft = activeExcsAtStart * excWidth + gutter;

                    return (
                      <div
                        key={a.id}
                        className="absolute z-10 transition-all duration-200"
                        style={{
                          top,
                          height,
                          left: `calc((${col} * (100% - ${offsetLeft}px) / ${cols}) + ${offsetLeft}px)`,
                          width: `calc(((100% - ${offsetLeft}px) / ${cols}) - ${gutter * 2}px)`,
                        }}
                      >
                        <AppointmentCard
                          appt={a as any}
                          onEdit={() => onEdit && onEdit(a as any)}
                          onMarkDone={() => onMarkDone && onMarkDone(a)}
                          onUnmarkDone={() => onUnmarkDone && onUnmarkDone(a)}
                          onDelete={() => onDelete && onDelete(a)}
                          onFillEvolution={() => onFillEvolution && onFillEvolution(a)}
                          showStatusActions={true}
                          bgColor={bg}
                          className="h-full text-[10px] overflow-hidden border-0 shadow-sm rounded-lg transition-shadow"
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
